import clsx from 'clsx';
import { FunctionComponent, PropsWithChildren } from 'react';
import { createPortal } from 'react-dom';

import styles from './FullWindowModal.module.scss';

interface FullWindowModalProps extends PropsWithChildren {
    className?: string;
    /**
     * Optional z-index override. Defaults to 10001 to match the legacy
     * `OverFullWindow` component so stacking behavior against any open
     * MUI-based modal stays predictable while the migration is in progress.
     */
    zIndex?: number;
}

/**
 * V3 full-window takeover container. The composable counterpart to the
 * legacy Material-UI `OverFullWindow`. Use this for "the wizard fills the
 * screen" / "previewing content at near-true size" kinds of UI where a
 * centered modal would feel cramped.
 *
 * Composable shape:
 *
 *   <FullWindowModal>
 *     <FullWindowModalHeader>…</FullWindowModalHeader>
 *     <FullWindowModalBody>…</FullWindowModalBody>
 *     <FullWindowModalFooter>…</FullWindowModalFooter>
 *   </FullWindowModal>
 *
 * Each slot ships with the default chrome (66px min-height, white bg,
 * borders) so consumers don't need to redefine it. Override via the
 * `className` prop on any slot.
 */
export const FullWindowModal: FunctionComponent<FullWindowModalProps> = ({ children, className, zIndex }) => {
    return createPortal(
        <div
            className={clsx(styles.root, className)}
            style={zIndex !== undefined ? { zIndex } : undefined}
        >
            {children}
        </div>,
        document.body
    );
};

export default FullWindowModal;
